import { motion } from 'framer-motion';

// Wall geometry lifted verbatim from img/renaissance-new/maze.svg so the
// walls match the site's canonical maze artwork exactly.
const WALLS_D = `
  M279 217L279 187.5L248.5 187.5M248.5 187.5L248.5 159M248.5 187.5L187 187.5M187 187.5L187 249.5L215 249.5M187 187.5L187 158.25M187 129L187 158.25M187 158.25L125 158.25M63 158.25L63 127L63 187.5M35.5 187.5L63 187.5L63 158.25M63 158.25L94 158.25M125 158.25L125 217.5L93 217.5M125 158.25L94 158.25M93 217.5L93 190.5M93 217.5L93 249.5M93 249.5L63 249.5L63 277.5M93 249.5L93 280.5L124 280.5L124 311M124 311L153 311M124 311L32 311M32 311L32 254M32 311L1.49999 311L1.49999 217.5M1.49999 217.5L60 217.5M1.49999 217.5L1.5 94.5M1.5 94.5L30.75 94.5M1.5 94.5L1.5 32.5M60 94.5L30.75 94.5M30.75 94.5L30.75 152.5M1.5 32.5L30.75 32.5M1.5 32.5L1.5 1.49999L125 1.49999L125 32.5M125 61L125 32.5M125 32.5L95.5 32.5M94 158.25L94 94.5M94 94.5L122.5 94.5M94 94.5L94 64.5L62.5 64.5M35.5 64.5L62.5 64.5M62.5 64.5L62.5 34
  M158 1.99999L310.5 2L310.5 312L187 312L187 280L156 280L156 189
  M187 280L215.5 280
  M248.5 312L248.5 217.5L220 217.5
  M248.5 249L277 249
  M310.5 280L280.5 280
  M310.5 157L278.5 157L278.5 126L217.5 126L217.5 97.5L217.5 153
  M278.5 126L278.5 95L250.5 95
  M156 249L127 249
  M249 2L249 32.5L276.5 32.5
  M186 2L186 33.5L156 33.5L156 95L186 95L186 64.5L277 64.5
  M156 95L156 126L127 126
  M217.5 64.5L217.5 34
`;

// Centerline of the real solution route through the maze above, derived
// from the filled solved-path shape in maze-with-mentorship.svg (same maze,
// translated into maze.svg's coordinate space) so the drawn line always
// stays inside the actual corridors instead of crossing walls.
const SOLUTION_D =
  'M138,1.5 L138,107 L135,110 L109.5,110 L106.5,113 L106.5,140.5 L109.5,143.5 L168.5,143.5 L171.5,140.5 L171.5,113 L174.5,110 L199.5,110 L202.5,113 L202.5,167 L205.5,170 L229.5,170 L232.5,167 L232.5,143 L235.5,140 L260,140 L263,143 L263,167 L266,170 L294,170 L297,173 L297,228 L294,231 L270,231 L267,228 L267,207 L264,204 L205.5,204 L202.5,207 L202.5,232 L205.5,235 L229.5,235 L232.5,238 L232.5,261.5 L229.5,264.5 L174.5,264.5 L171.5,261.5 L171.5,176 L168.5,173 L141,173 L138,176 L138,232 L135,235 L109.5,235 L106.5,238 L106.5,261.5 L109.5,264.5 L135,264.5 L138,267.5 L138,293 L141,296 L164,296 L167,299 L167,306';

const DRAW_DURATION = 2.7;
const HOLD_DURATION = 1;
const CYCLE_DURATION = DRAW_DURATION + HOLD_DURATION;

const AnimatedMaze = () => (
  <svg
    viewBox="0 0 312 335"
    className="w-full h-auto max-w-[312px]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      {/* Matches the design spec: box-shadow: 0px 1px 6px #4659CA */}
      <filter id="maze-path-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="1" stdDeviation="3" floodColor="#4659CA" floodOpacity="0.9" />
      </filter>
    </defs>

    <path d={WALLS_D} stroke="#94A3B8" strokeWidth="3" />

    {/* Solved path: border: 3px solid #3C48B9; border-radius: 3px */}
    <motion.path
      d={SOLUTION_D}
      stroke="#3C48B9"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      filter="url(#maze-path-glow)"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{
        duration: DRAW_DURATION,
        ease: 'easeInOut',
        repeat: Infinity,
        repeatType: 'loop',
        repeatDelay: HOLD_DURATION,
      }}
    />

    {/* Arrowhead: fades in once the path finishes drawing, holds, then
        resets instantly in sync with the path looping back to empty. */}
    <motion.path
      d="M158.3,304.5 L175.7,304.5 L167,319.5 Z"
      fill="#3C48B9"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0, 1, 1] }}
      transition={{
        duration: CYCLE_DURATION,
        times: [0, (DRAW_DURATION - 0.15) / CYCLE_DURATION, DRAW_DURATION / CYCLE_DURATION, 1],
        repeat: Infinity,
        repeatType: 'loop',
        ease: 'easeOut',
      }}
    />
  </svg>
);

export default AnimatedMaze;
